var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { FaTimes, FaPlus, FaMinus, FaCrosshairs } from 'react-icons/fa';
import { backendUrl } from '@common/contracts';
import { assetUrl } from '../../utils/img';
import { getCoordinateString } from '../../utils/coordinates';
import { backgroundOnGridStyle } from '../../utils/mapGeometry';
import SegmentedControl from '../SegmentedControl/SegmentedControl';
import { getNodeImages } from '../../api/images';
import './MapPickerModal.scss';
const MapPickerModal = ({ isOpen, onClose, levelData, selectedNodeCode, onNodeSelect }) => {
    const [viewMode, setViewMode] = useState('Code');
    const [zoomLevel, setZoomLevel] = useState(100);
    const [nodeImages, setNodeImages] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [hasMoved, setHasMoved] = useState(false);
    const [pendingCenter, setPendingCenter] = useState(false);
    const mapContainerRef = React.useRef(null);
    const handleNodeClick = (nodeCode) => {
        // Prevent node selection if a drag attempt occurred
        if (hasMoved)
            return;
        onNodeSelect(nodeCode);
        onClose();
    };
    // Get the node image by its map coord ("A1")
    const getNodeImage = (nodeCode) => {
        const image = nodeImages.find(img => img.coord === nodeCode);
        return image ? image.imageUrl : null;
    };
    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };
    const handleZoomIn = () => {
        setZoomLevel(prev => Math.min(prev + 25, 300));
    };
    const handleZoomOut = () => {
        setZoomLevel(prev => Math.max(prev - 25, 50));
    };
    const centerMapPosition = () => {
        const container = mapContainerRef.current;
        if (!container)
            return;
        const wrapper = container.querySelector('.map-wrapper');
        if (!wrapper)
            return;
        // Current center coordinates of the wrapper and container relative to the viewport
        const containerRect = container.getBoundingClientRect();
        const wrapperRect = wrapper.getBoundingClientRect();
        const deltaX = (wrapperRect.left + wrapperRect.width / 2) - (containerRect.left + containerRect.width / 2);
        const deltaY = (wrapperRect.top + wrapperRect.height / 2) - (containerRect.top + containerRect.height / 2);
        container.scrollLeft += deltaX;
        container.scrollTop += deltaY;
    };
    const handleCenterMap = () => {
        if (zoomLevel !== 100) {
            setZoomLevel(100);
            setPendingCenter(true);
        }
        else {
            centerMapPosition();
        }
    };
    // Reset the map position when the modal opens
    React.useEffect(() => {
        if (isOpen) {
            setZoomLevel(100);
        }
    }, [isOpen]);
    useEffect(() => {
        if (pendingCenter) {
            centerMapPosition();
            setPendingCenter(false);
        }
    }, [zoomLevel, pendingCenter]);
    const handleViewModeChange = (newMode) => {
        setViewMode(newMode);
    };
    const handleMapMouseDown = (e) => {
        if (e.button === 0) { // Left mouse button only
            const container = e.currentTarget;
            setIsDragging(true);
            setHasMoved(false); // Reset the movement flag
            setDragStart({
                x: e.clientX + container.scrollLeft,
                y: e.clientY + container.scrollTop
            });
            e.preventDefault();
        }
    };
    const handleMapMouseMove = (e) => {
        if (isDragging) {
            const container = e.currentTarget;
            const newScrollLeft = dragStart.x - e.clientX;
            const newScrollTop = dragStart.y - e.clientY;
            // Determine whether significant movement occurred (more than 3px)
            const deltaX = Math.abs(newScrollLeft - container.scrollLeft);
            const deltaY = Math.abs(newScrollTop - container.scrollTop);
            if (deltaX > 3 || deltaY > 3) {
                setHasMoved(true);
            }
            // Remove constraints - allow dragging in any direction
            container.scrollLeft = newScrollLeft;
            container.scrollTop = newScrollTop;
        }
    };
    const handleMapMouseUp = () => {
        setIsDragging(false);
        // Reset the movement flag after a short delay to prevent accidental clicks
        setTimeout(() => {
            setHasMoved(false);
        }, 100);
    };
    const handleMapMouseLeave = () => {
        setIsDragging(false);
        // Reset the movement flag after a short delay
        setTimeout(() => {
            setHasMoved(false);
        }, 100);
    };
    // Load the node images when the modal opens
    useEffect(() => {
        if (isOpen && (levelData === null || levelData === void 0 ? void 0 : levelData.id)) {
            const loadNodeImages = () => __awaiter(void 0, void 0, void 0, function* () {
                try {
                    const images = yield getNodeImages(levelData.id);
                    setNodeImages(images);
                }
                catch (error) {
                    console.error('Error loading node images:', error);
                    setNodeImages([]);
                }
            });
            loadNodeImages();
        }
    }, [isOpen, levelData === null || levelData === void 0 ? void 0 : levelData.id]);
    if (!isOpen || !levelData)
        return null;
    // Compute the base cell size so the map fits at 100% zoom
    const maxContainerWidth = Math.min(window.innerWidth * 0.95, 1200) - 32; // Account for header padding
    const maxContainerHeight = Math.min(window.innerHeight * 0.95, 1000) - 80; // Account for header height
    const baseCellSize = Math.min(maxContainerWidth / levelData.mapWidth, maxContainerHeight / levelData.mapHeight);
    return (_jsx("div", { className: "map-picker-modal-overlay", onClick: handleOverlayClick, children: _jsxs("div", { className: "map-picker-modal", children: [_jsxs("div", { className: "map-picker-header", children: [_jsxs("div", { className: "map-picker-controls", children: [_jsx("div", { className: "view-mode-control", children: _jsx(SegmentedControl, { options: [
                                            { value: 'Code', label: 'Code' },
                                            { value: 'Map', label: 'Map' },
                                            { value: 'Art', label: 'Art' }
                                        ], value: viewMode, name: "mapViewMode", onChange: handleViewModeChange }) }), _jsxs("div", { className: "zoom-controls", children: [_jsx("button", { type: "button", className: "zoom-button", onClick: handleZoomOut, disabled: zoomLevel <= 50, title: "Zoom Out", children: _jsx(FaMinus, {}) }), _jsxs("span", { className: "zoom-level", children: [Math.round(zoomLevel), "%"] }), _jsx("button", { type: "button", className: "zoom-button", onClick: handleZoomIn, disabled: zoomLevel >= 300, title: "Zoom In", children: _jsx(FaPlus, {}) }), _jsx("button", { type: "button", className: "center-button", onClick: handleCenterMap, title: "Center Map (100%)", children: _jsx(FaCrosshairs, {}) })] })] }), _jsx("button", { type: "button", className: "close-button", onClick: onClose, children: _jsx(FaTimes, {}) })] }), _jsx("div", { className: "map-picker-content", children: _jsx("div", { ref: mapContainerRef, className: "map-container", onMouseDown: handleMapMouseDown, onMouseMove: handleMapMouseMove, onMouseUp: handleMapMouseUp, onMouseLeave: handleMapMouseLeave, style: {
                            cursor: isDragging ? 'grabbing' : 'grab'
                        }, children: _jsx("div", { className: "map-scroll-area", children: _jsxs("div", { className: "map-wrapper", style: {
                                    '--map-width': levelData.mapWidth,
                                    '--map-height': levelData.mapHeight,
                                    '--cell-size': `${baseCellSize * (zoomLevel / 100)}px`,
                                    width: `${levelData.mapWidth * baseCellSize * (zoomLevel / 100)}px`,
                                    height: `${levelData.mapHeight * baseCellSize * (zoomLevel / 100)}px`
                                }, children: [viewMode !== 'Art' && (_jsx("div", { className: "map-background", style: Object.assign({ backgroundImage: levelData.backgroundImage
                                                ? `url(${levelData.backgroundImage.startsWith('http') ? levelData.backgroundImage : `${backendUrl}${levelData.backgroundImage}`})`
                                                : 'none' }, backgroundOnGridStyle(levelData.mapWidth)) })), _jsx("div", { className: "map-grid", children: Array.from({ length: levelData.mapHeight }, (_, y) => Array.from({ length: levelData.mapWidth }, (_, x) => {
                                            const nodeCode = getCoordinateString(x, y);
                                            const isSelected = selectedNodeCode === nodeCode;
                                            const nodeImageUrl = getNodeImage(nodeCode);
                                            return (_jsxs("div", { className: `map-cell ${isSelected ? 'selected' : ''} ${nodeImageUrl ? 'has-image' : ''}`, onClick: (e) => {
                                                    e.stopPropagation();
                                                    handleNodeClick(nodeCode);
                                                }, title: `${nodeCode} (${x + 1}, ${y + 1})`, children: [viewMode === 'Art' && nodeImageUrl && (_jsx("img", { src: assetUrl(nodeImageUrl), alt: `Node ${nodeCode}`, className: "cell-image" })), viewMode === 'Code' && (_jsx("span", { className: "cell-code", children: nodeCode }))] }, `${x}-${y}`));
                                        })).flat() })] }) }) }) })] }) }));
};
export default MapPickerModal;
